import { propertyAgencyCode } from '@/shared/utils/property-display';
import { DEFAULT_VALUES } from './schema';
import { withPricedTransactions } from './priced-transactions';
import { normalizeLegacyAmenities } from './legacy-amenity-labels';
import type { PropertyFormValues } from './schema';

/**
 * Coerces an unknown source value to a string for form fields.
 * Nullish values become empty string; numbers (e.g. address number from the API)
 * become their decimal representation so zod string validators accept them.
 */
function str(value: unknown): string {
  if (value == null) return '';
  return String(value);
}

/**
 * Extracts text and address fields from the nested property response.
 */
function pickStringFields(p: Record<string, any>) {
  const code = propertyAgencyCode(p) ?? '';
  const location = p.location ?? {};
  const description = p.description ?? {};
  const classification = p.classification ?? {};
  const chars = p.characteristics ?? {};
  return {
    category: str(classification.category),
    postal_code: str(location.postal_code),
    street: str(location.street),
    number: str(location.number),
    complement: str(location.complement),
    neighborhood: str(location.neighborhood),
    city: str(location.city),
    state: str(location.state),
    property_title: str(description.headline),
    property_code: code,
    property_code_initial: code,
    description: str(description.full_text ?? description.full_description),
    furnished: emptyToUndef(str(chars.furnished)),
    facing: emptyToUndef(str(chars.facing)),
    parking_type: emptyToUndef(str(chars.parking_type)),
  };
}

/**
 * Extracts numeric fields from the nested property response.
 */
function pickNumericFields(p: Record<string, any>) {
  const chars = p.characteristics ?? {};
  const pricing = p.pricing ?? {};
  const condo = p.condominium ?? {};
  return {
    total_area: chars.total_area ?? undefined,
    constructed_area: chars.constructed_area ?? undefined,
    sale_price: pricing.sale?.price ?? undefined,
    rental_price: pricing.rental?.price ?? undefined,
    condominium_price: condo.monthly_fee ?? undefined,
    iptu: normalizeIptu(pricing.taxes?.iptu_annual ?? pricing.sale?.iptu),
    bedrooms: chars.bedrooms ?? undefined,
    suites: chars.suites ?? undefined,
    bathrooms: chars.bathrooms ?? undefined,
    parking_spots: chars.parking_spots ?? undefined,
    floor: chars.floor_number ?? chars.floor ?? undefined,
    year_built: chars.year_built ?? undefined,
    admin_percentage: undefined,
    lease_term_in_months: pricing.rental?.min_contract_months ?? 30,
  };
}

/**
 * Normalizes the IPTU field to a single numeric value.
 */
function normalizeIptu(iptu: unknown): number | undefined {
  if (Array.isArray(iptu)) return Number(iptu[0]) || undefined;
  return (iptu as number) ?? undefined;
}

/**
 * Reports whether a value is a non-null object.
 */
function isObject(value: unknown): boolean {
  return typeof value === 'object' && value !== null;
}

/**
 * Extracts the boolean toggle fields from the nested property response.
 */
function pickBooleanFields(p: Record<string, any>) {
  const chars = p.characteristics ?? {};
  return {
    fixed_parking: chars.fixed_parking ?? false,
    gourmet_balcony: chars.gourmet_balcony ?? false,
    balcony: chars.balcony ?? false,
    accepts_pets: chars.accepts_pets ?? false,
    condominium: isObject(p.condominium),
  };
}

/**
 * Returns the first array-typed argument that is present.
 */
function arr(...candidates: unknown[]): string[] {
  const found = candidates.find((c) => Array.isArray(c));
  return (found as string[]) ?? [];
}

/**
 * Normalizes a guarantee value that may arrive as a string or string array.
 */
function asStringList(value: unknown): string[] {
  if (Array.isArray(value)) return value as string[];
  if (typeof value === 'string' && value.length > 0) return [value];
  return [];
}

/**
 * Extracts array fields from the nested property response.
 */
function pickArrayFields(p: Record<string, any>) {
  const amenities = p.amenities ?? {};
  const classification = p.classification ?? {};
  const condo = p.condominium ?? {};
  const pricing = p.pricing ?? {};
  const metadata = p.metadata ?? {};
  return {
    transaction_type: arr(classification.transaction_types),
    leisure_areas: normalizeLegacyAmenities(arr(amenities.leisure_areas, amenities.external)),
    facilities: normalizeLegacyAmenities(arr(amenities.facilities, amenities.internal)),
    security: normalizeLegacyAmenities(arr(amenities.security)),
    infrastructure: arr(amenities.infrastructure),
    highlights: arr(p.description?.highlights),
    condo_amenities: normalizeLegacyAmenities(arr(condo.leisure_areas)),
    condo_security: normalizeLegacyAmenities(arr(condo.security)),
    condo_included_expenses: arr(condo.included_expenses),
    condo_paid_by: emptyToUndef(str(condo.paid_by)),
    rental_guarantee: asStringList(pricing.rental?.guarantee_type),
    rental_included_expenses: arr(pricing.rental?.included_expenses),
    tags: arr(metadata.tags),
  };
}

/**
 * Converts empty strings to undefined for optional form fields.
 *
 * @param value - Coerced string value.
 * @returns Undefined when empty; otherwise the value.
 */
function emptyToUndef(value: string): string | undefined {
  if (value === '') return undefined;
  return value;
}

/**
 * Extracts ownership, documentation and media fields for edit hydration.
 *
 * @param p - Nested property payload from the API.
 * @returns Ownership, mandate, documentation and rental meta fields.
 */
function pickNestedMetaFields(p: Record<string, any>) {
  const ownership = p.ownership ?? {};
  const mandate = ownership.mandate ?? {};
  const documentation = p.documentation ?? {};
  const pricing = p.pricing ?? {};
  const assignment = p.assignment ?? {};
  return {
    captured_by: emptyToUndef(str(assignment.captured_by)),
    primary_broker: emptyToUndef(str(assignment.primary_broker)),
    assigned_brokers: arr(assignment.assigned_brokers),
    owner_client_id: emptyToUndef(str(ownership.owner_client_id)),
    mandate_type: emptyToUndef(str(mandate.mandate_type)),
    mandate_start_date: emptyToUndef(str(mandate.start_date)),
    mandate_end_date: emptyToUndef(str(mandate.end_date)),
    mandate_commission: mandate.commission_percentage ?? undefined,
    registration_number: emptyToUndef(str(documentation.registration_number)),
    registry_office: emptyToUndef(str(documentation.registry_office)),
    inscription_number: emptyToUndef(str(documentation.inscription_number)),
    virtual_tour_url: emptyToUndef(str(p.media?.virtual_tour_url)),
    video_links: arr(p.media?.videos),
    rental_payment_frequency: emptyToUndef(str(pricing.rental?.payment_frequency)),
    administration_type: emptyToUndef(str(pricing.rental?.administration)),
    accepts_exchange: pricing.sale?.accepts_exchange === true,
  };
}

/**
 * Reads a stored number, leaving anything else (null, text) as not informed.
 *
 * @param value - The stored value.
 * @returns The number, or undefined.
 */
function num(value: unknown): number | undefined {
  return typeof value === 'number' ? value : undefined;
}

/**
 * Reads a stored tri-state flag into the select's 'yes' / 'no'.
 *
 * @param value - The stored boolean, absent when not informed.
 * @returns 'yes', 'no', or undefined.
 */
function flag(value: unknown): string | undefined {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return undefined;
}

/**
 * Extracts the Univen text attributes (plan §3.1).
 *
 * @param p - Nested property payload from the API.
 * @returns Location, condominium, characteristics and ownership text fields.
 */
function pickAttributeTextFields(p: Record<string, any>) {
  const location = p.location ?? {};
  const condo = p.condominium ?? {};
  const chars = p.characteristics ?? {};
  const ownership = p.ownership ?? {};
  const text = (value: unknown) => emptyToUndef(str(value));
  return {
    condominium_name: text(condo.name),
    tower_name: text(p.category_specifics?.tower_name),
    development_name: text(condo.development_name),
    developer: text(condo.developer),
    block: text(location.block),
    unit: text(location.unit),
    quadra: text(location.quadra),
    lot: text(location.lot),
    condition: text(chars.condition),
    occupancy: text(chars.occupancy),
    finish_standard: text(chars.finish_standard),
    location_quality: text(chars.location_quality),
    exclusive: flag(ownership.exclusive),
    exclusive_until: text(ownership.exclusive_until),
    publish_authorized: flag(ownership.publish_authorized),
    publish_authorized_until: text(ownership.publish_authorized_until),
    key_location: text(ownership.key_location),
    partner_name: text(ownership.partner_name),
  };
}

/**
 * Extracts the Temporada and Leilão pricing plus the distance to the sea.
 *
 * @param p - Nested property payload from the API.
 * @returns The numeric and date attributes.
 */
function pickAttributePricingFields(p: Record<string, any>) {
  const shortTerm = p.pricing?.short_term ?? {};
  const auction = p.pricing?.auction ?? {};
  return {
    distance_to_sea_m: num(p.location?.distance_to_sea_m),
    short_term_price: num(shortTerm.price),
    short_term_guests: num(shortTerm.guests),
    short_term_cleaning_fee: num(shortTerm.cleaning_fee),
    auction_first_date: emptyToUndef(str(auction.first_date)),
    auction_first_price: num(auction.first_price),
    auction_second_date: emptyToUndef(str(auction.second_date)),
    auction_second_price: num(auction.second_price),
  };
}

/**
 * Builds initial form values: returns the schema defaults when creating, or a
 * property mapped through the field pickers when editing.
 *
 * A stored short-term or auction price lights its transaction chip even on a
 * record written before the backend marked it, so its block is never hidden.
 */
export function buildDefaultValues(editingProperty?: Record<string, any> | null): PropertyFormValues {
  if (!editingProperty) return DEFAULT_VALUES;
  const pricing = pickAttributePricingFields(editingProperty);
  const arrays = pickArrayFields(editingProperty);
  return {
    ...pickStringFields(editingProperty),
    ...pickNumericFields(editingProperty),
    ...pickBooleanFields(editingProperty),
    ...arrays,
    ...pickNestedMetaFields(editingProperty),
    ...pickAttributeTextFields(editingProperty),
    ...pricing,
    transaction_type: withPricedTransactions(arrays.transaction_type, pricing),
  };
}
